import { CategoryResponse } from '@business/entities/category';
import { CategoryOptionId } from '@business/entities/category_option';
import { SearchProductResponse } from '@business/entities/order';
import {
  EstimateGroupResponse,
  ServiceProviderId,
  TMChangeOrderLineResponse,
  TMChangeOrderResponse,
  TMChangeOrderSaveAction,
  TMChangeOrderStatus,
  TMChangeOrderTypeResponse,
} from '@business/entities/tm';
import { R } from '@core/utils/r';
import { ChangeOrderFormData, ChangeOrderLineItemData } from '@modules/timeMaterials/AddEditTMChangeOrderPage/TMChangeOrderForm';

// The vendor's price for a product, for the option too when the line has one
export function getProductPrice(product: SearchProductResponse, providerId: ServiceProviderId, optionId?: CategoryOptionId) {
  return R.find(product.providers ?? [], p => p.providerId === providerId && (!optionId || p.optionId === optionId));
}

// The line's category for a product: its top-level category, preferring one that's on the Work Order
export function getProductCategoryId(product: SearchProductResponse, categories: CategoryResponse[], groups: Pick<EstimateGroupResponse, 'category'>[]) {
  const topCategories = product.categories.map(category => {
    const parent = category.parentId && category.parentId !== '0' ? R.find(categories, c => c.id == category.parentId) : null;
    return parent ?? category;
  });
  const onTheJob = R.find(topCategories, category => groups.some(group => group.category?.id == category.id));
  return (onTheJob ?? topCategories[0])?.id;
}

// for calculations
export function getSubTotal(item: ChangeOrderLineItemData) {
  const laborPrice = item.quantity * item.laborPrice || 0;
  const materialPrice = item.quantity * item.materialPrice || 0;
  return laborPrice + materialPrice;
  // return item.quantity * item.unitPrice;
}

export function getItemMarkup(item: ChangeOrderLineItemData) {
  return getSubTotal(item) * (item.markupFactor / 100);
}

export function getItemDependantFactor(item: ChangeOrderLineItemData) {
  return getSubTotal(item) * (item.dependantFactor / 100);
}

export function getItemTotal(item: ChangeOrderLineItemData) {
  return getSubTotal(item) + getItemMarkup(item) + getItemDependantFactor(item);
}

export function getChangeOrderSubTotal(item: ChangeOrderFormData) {
  return item.lineItems.reduce((acc, line) => acc + getSubTotal(line), 0);
}

export function getChangeOrderTotal(item: ChangeOrderFormData) {
  return item.lineItems.reduce((acc, line) => acc + getItemTotal(line), 0);
}

// $0 types: the lines' amount (with factor) and their markup must both net to $0.00 (TMChangeOrder::netsToZero)
export function getChangeOrderAmountIncFactor(item: ChangeOrderFormData) {
  return item.lineItems.reduce((acc, line) => acc + getSubTotal(line) * (1 + (line.dependantFactor || 0) / 100), 0);
}

export function getChangeOrderMarkup(item: ChangeOrderFormData) {
  return item.lineItems.reduce((acc, line) => acc + getSubTotal(line) * ((line.markupFactor || 0) / 100), 0);
}

export function netsToZero(item: ChangeOrderFormData) {
  return Math.abs(getChangeOrderAmountIncFactor(item)) < 0.005 && Math.abs(getChangeOrderMarkup(item)) < 0.005;
}
// end form calculations

export interface ChangeOrderSaveButton {
  action: TMChangeOrderSaveAction;
  title: string;
}

/**
 * The change order form's buttons for a type: the same rules as TMChangeOrderType::statusAfterSave on the server.
 * - subcontractors: Submit (Under Review);
 * - staff: Save as Draft on every type, except on one Under Review (it can't go back to Draft), plus:
 *   - a type that needs internal approval: Submit for Review (just Save when it's already Under Review), and Save &
 *     Approve or Save & Send for its approvers;
 *   - a type the client signs: Save & Send;
 *   - no approvals at all: Save & Approve.
 */
export function getSaveButtons(
  type: TMChangeOrderTypeResponse | undefined,
  isSubcontractor: boolean,
  status?: TMChangeOrderStatus,
): ChangeOrderSaveButton[] {
  if (!type) {
    return [];
  }
  if (isSubcontractor) {
    return [{ action: TMChangeOrderSaveAction.save, title: 'Submit' }];
  }

  const isUnderReview = status === TMChangeOrderStatus.pending;
  const saveAsDraft = isUnderReview ? [] : [{ action: TMChangeOrderSaveAction.draft, title: 'Save as Draft' }];
  const saveAndApprove = { action: TMChangeOrderSaveAction.approve, title: 'Save & Approve' };
  const saveAndSend = { action: TMChangeOrderSaveAction.send, title: 'Save & Send' };
  if (type.needsInternalApproval) {
    const saveForReview = { action: TMChangeOrderSaveAction.save, title: isUnderReview ? 'Save' : 'Submit for Review' };
    const approverButtons = type.can.approve ? [type.needsClientApproval ? saveAndSend : saveAndApprove] : [];
    return [...saveAsDraft, saveForReview, ...approverButtons];
  }
  if (type.needsClientApproval) {
    return [...saveAsDraft, saveAndSend];
  }
  return [...saveAsDraft, saveAndApprove];
}

// BACKEND RESPONSE CALCULATIONS
export function getSubTotalR(item: TMChangeOrderLineResponse) {
  const laborPrice = item.quantity * item.laborPrice || 0;
  const materialPrice = item.quantity * item.materialPrice || 0;
  return laborPrice + materialPrice;
  // return item.quantity * item.unitPrice;
}

export function getItemMarkupR(item: TMChangeOrderLineResponse) {
  return getSubTotalR(item) * (item.markupFactor / 100);
}

export function getItemTotalR(item: TMChangeOrderLineResponse) {
  return getSubTotalR(item) + getItemMarkupR(item);
}

export function getChangeOrderSubTotalR(item: TMChangeOrderResponse) {
  return item.items.reduce((acc, line) => acc + getSubTotalR(line), 0);
}

export function getChangeOrderTotalR(item: TMChangeOrderResponse) {
  return item.items.reduce((acc, line) => acc + getItemTotalR(line), 0);
}

// END BACKEND RESPONSE CALCULATIONS
